import type { DiaryEntryCreate } from "@/types/diaryCreate";
import type {
  ContactSummary,
  DiaryEntryFilter,
  DiaryEntrySummary,
  PropertySummary,
  UserSummary,
} from "@/types/entities";
import {
  isLocalDateInRange,
  parseApiDateTime,
  startOfLocalDay,
  toDateKey,
} from "@/utils/date-helpers";
import { WEEKDAY_CODE_BY_INDEX } from "@/types/availability";

const MAX_OCCURRENCES = 1000;

/**
 * Date keys (YYYY-MM-DD) on which a diary entry should appear within a visible window.
 * A one-off entry yields just its start day; a recurring entry (DAILY/WEEKLY + interval,
 * bounded by recurrenceUntil) is expanded across all its occurrence days in range — so
 * the calendar shows it on every recurring day, Google-Calendar style. Stepping starts
 * from the original start so the cadence is preserved even when it began before the window.
 */
export function diaryOccurrenceDateKeys(
  entry: Pick<
    DiaryEntrySummary,
    | "startDate"
    | "recurring"
    | "recurrenceType"
    | "recurrenceInterval"
    | "recurrenceUntil"
    | "recurrenceExceptions"
    | "recurrenceDaysOfWeek"
  >,
  rangeStart: Date,
  rangeEnd: Date,
): string[] {
  if (!entry.startDate) return [];
  const base = parseApiDateTime(entry.startDate);
  if (!base) return [];
  if (!entry.recurring || !entry.recurrenceType) {
    return [toDateKey(base)];
  }

  const freq = entry.recurrenceType.trim().toUpperCase();
  const interval = Math.max(1, entry.recurrenceInterval ?? 1);
  const until = entry.recurrenceUntil ? parseApiDateTime(entry.recurrenceUntil) : null;
  const windowStart = startOfLocalDay(rangeStart);
  const hardEnd = until && until < rangeEnd ? until : rangeEnd;
  const excluded = new Set(entry.recurrenceExceptions ?? []);
  const daysOfWeek = new Set(entry.recurrenceDaysOfWeek ?? []);
  const baseDay = startOfLocalDay(base);

  // WEEKLY with selected weekdays (e.g. Mon/Wed/Fri, or Mon–Fri to skip weekends):
  // walk day-by-day, emit on the chosen weekdays every `interval` weeks from the start week.
  if (freq === "WEEKLY" && daysOfWeek.size > 0) {
    const cursor = new Date(base);
    if (cursor < windowStart) {
      const blocksBehind = Math.floor(
        Math.round((windowStart.getTime() - baseDay.getTime()) / 86400000) / 7,
      );
      const jump = Math.floor(blocksBehind / interval) * interval; // keep the week phase
      if (jump > 0) cursor.setDate(cursor.getDate() + jump * 7);
    }
    const keys: string[] = [];
    let guard = 0;
    while (cursor <= hardEnd && guard++ < MAX_OCCURRENCES) {
      if (cursor >= windowStart) {
        const code = WEEKDAY_CODE_BY_INDEX[cursor.getDay()];
        const weeksFromBase = Math.floor(
          Math.round((startOfLocalDay(cursor).getTime() - baseDay.getTime()) / 86400000) / 7,
        );
        if (daysOfWeek.has(code) && weeksFromBase % interval === 0) {
          const key = toDateKey(cursor);
          if (!excluded.has(key)) keys.push(key);
        }
      }
      cursor.setDate(cursor.getDate() + 1);
    }
    return keys;
  }

  const stepDays = (freq === "WEEKLY" ? 7 : 1) * interval;
  const cursor = new Date(base);
  // Fast-forward to just before the window so iterations stay bounded for old recurrences.
  if (cursor < windowStart) {
    const daysBehind = Math.floor((windowStart.getTime() - cursor.getTime()) / 86400000);
    const skip = Math.floor(daysBehind / stepDays);
    if (skip > 0) cursor.setDate(cursor.getDate() + skip * stepDays);
  }

  const keys: string[] = [];
  let guard = 0;
  while (cursor <= hardEnd && guard++ < MAX_OCCURRENCES) {
    if (cursor >= windowStart) {
      const key = toDateKey(cursor);
      if (!excluded.has(key)) keys.push(key); // skip user-excluded occurrence days
    }
    cursor.setDate(cursor.getDate() + stepDays);
  }
  return keys;
}

/** Last occurrence date (YYYY-MM-DD) on or before {@link entry.recurrenceUntil}. */
export function lastRecurrenceOccurrenceDateKey(
  entry: Parameters<typeof diaryOccurrenceDateKeys>[0],
): string | null {
  const base = entry.startDate ? parseApiDateTime(entry.startDate) : null;
  if (!base) return null;
  if (!entry.recurring || !entry.recurrenceType) return toDateKey(base);
  const until = entry.recurrenceUntil ? parseApiDateTime(entry.recurrenceUntil) : null;
  if (!until) return null;
  const keys = diaryOccurrenceDateKeys(entry, base, until);
  return keys.length > 0 ? keys[keys.length - 1]! : null;
}

/** Google Calendar-style "which calendars are visible" state for the Diary page. */
export interface DiaryCalendarFilterState {
  /** Other users' calendars (admin-only) -> attendeeId. */
  userIds: string[];
  /** Current user's own calendar -> attendeeId = currentUser.id. */
  includeMe: boolean;
  /** "All Events" toggle -> when true, no user filtering is applied. */
  includeAllEvents: boolean;
  /** Existing "Properties" behaviour -> propertyId. */
  propertyIds: string[];
  /** Property availability calendars -> propertyId + entryType=AVAILABILITY. */
  availabilityPropertyIds: string[];
  /** Optional exact event date (YYYY-MM-DD). */
  date: string;
  /** Origin inferred from Atlas audit metadata; empty means either origin. */
  createdBy: "" | "BRIDGE_AI" | "CRM";
}

export const DEFAULT_DIARY_CALENDAR_FILTERS: DiaryCalendarFilterState = {
  userIds: [],
  includeMe: true,
  includeAllEvents: true,
  propertyIds: [],
  availabilityPropertyIds: [],
  date: "",
  createdBy: "",
};

/** Default calendar filters — negotiators only see their own diary (no "All Events"). */
export function diaryCalendarFiltersDefault(isNegotiator = false): DiaryCalendarFilterState {
  if (!isNegotiator) return DEFAULT_DIARY_CALENDAR_FILTERS;
  return { ...DEFAULT_DIARY_CALENDAR_FILTERS, includeAllEvents: false };
}

/** True when the filter state is the default (no extra calendars toggled). */
export function isDefaultDiaryCalendarFilters(
  filters: DiaryCalendarFilterState,
  isNegotiator = false,
): boolean {
  const expected = diaryCalendarFiltersDefault(isNegotiator);
  return (
    filters.userIds.length === 0 &&
    filters.includeMe === expected.includeMe &&
    filters.includeAllEvents === expected.includeAllEvents &&
    filters.propertyIds.length === 0 &&
    filters.availabilityPropertyIds.length === 0 &&
    !filters.date &&
    !filters.createdBy
  );
}

function hasCustomDiaryCalendars(filters: DiaryCalendarFilterState): boolean {
  return (
    filters.userIds.length > 0 ||
    filters.propertyIds.length > 0 ||
    filters.availabilityPropertyIds.length > 0
  );
}

/** Apply a filter patch; uncheck default "My Diary" toggles when custom calendars are selected. */
export function applyDiaryCalendarFilterPatch(
  current: DiaryCalendarFilterState,
  patch: Partial<DiaryCalendarFilterState>,
): DiaryCalendarFilterState {
  const next: DiaryCalendarFilterState = { ...current, ...patch };
  if (!hasCustomDiaryCalendars(next)) return next;
  return {
    ...next,
    includeAllEvents: false,
    includeMe: false,
  };
}

export interface DiaryFetchPlan {
  /** Property-based specs that still need separate API calls. */
  propertySpecs: Partial<DiaryEntryFilter>[];
  /** User IDs to filter by. Empty = show all entries (no user filtering). */
  allowedUserIds: Set<string>;
  /** When true, skip user filtering entirely. */
  includeAllEvents: boolean;
  /** Me only (no team members) — scope via userId on the API. */
  meOnly: boolean;
  /** Resolved Atlas user id for the logged-in user when Me is active. */
  meUserId?: string;
}

export function buildDiaryFetchPlan(
  filters: DiaryCalendarFilterState,
  currentUserId?: string | null,
): DiaryFetchPlan {
  const allowedUserIds = new Set<string>();
  if (filters.includeMe && currentUserId) allowedUserIds.add(currentUserId);
  for (const userId of filters.userIds) allowedUserIds.add(userId);

  const propertySpecs: Partial<DiaryEntryFilter>[] = [];
  for (const propertyId of filters.propertyIds) propertySpecs.push({ propertyId });
  for (const propertyId of filters.availabilityPropertyIds) {
    propertySpecs.push({ propertyId, entryType: "AVAILABILITY" });
  }

  const meOnly = filters.includeMe && filters.userIds.length === 0;

  return {
    propertySpecs,
    allowedUserIds,
    includeAllEvents: filters.includeAllEvents,
    meOnly,
    meUserId: meOnly && currentUserId ? currentUserId : undefined,
  };
}

/** Prepare list query params from calendar filter state. */
export function diaryFilterForApi(
  filter: DiaryEntryFilter,
  plan: DiaryFetchPlan,
): DiaryEntryFilter {
  const { userId: _omit, ...rest } = filter;
  if (plan.includeAllEvents) return rest;
  if (plan.meOnly && plan.meUserId) return { ...filter, userId: plan.meUserId };
  // Other team-member filters are applied client-side (or via property-scoped fetches).
  if (plan.allowedUserIds.size > 0) return rest;
  return filter;
}

type DiaryListAll = (
  filter: DiaryEntryFilter,
  pageBase: Omit<import("@/types/api").PageRequest, "page" | "size">,
) => Promise<DiaryEntrySummary[]>;

/**
 * Load diary rows for the active calendar filter plan.
 * - All Events: one legal-entity fetch.
 * - Properties only: fetch each propertyId (and optional entryType) from the API.
 * - Users only: legal-entity fetch, then client-side user filter (unless Me only → userId on API).
 * - Users + properties: property-scoped fetches only, then client-side user filter (intersection).
 */
export async function fetchDiaryEntriesForPlan(
  baseFilter: DiaryEntryFilter,
  plan: DiaryFetchPlan,
  sortParams: Omit<import("@/types/api").PageRequest, "page" | "size">,
  listAll: DiaryListAll,
): Promise<DiaryEntrySummary[]> {
  const apiFilter = diaryFilterForApi(baseFilter, plan);
  const hasUsers = plan.allowedUserIds.size > 0;
  const hasProperties = plan.propertySpecs.length > 0;

  if (plan.includeAllEvents && !hasProperties) {
    return listAll(apiFilter, sortParams);
  }

  if (hasProperties) {
    const rows = await Promise.all(
      plan.propertySpecs.map((spec) => listAll({ ...apiFilter, ...spec }, sortParams)),
    );
    const merged = dedupeDiaryEntries(rows.flat());
    if (hasUsers && !plan.meOnly) {
      return filterDiaryEntriesByUser(merged, plan.allowedUserIds, false);
    }
    return merged;
  }

  if (hasUsers) {
    const rows = await listAll(apiFilter, sortParams);
    if (plan.meOnly) return rows;
    return filterDiaryEntriesByUser(rows, plan.allowedUserIds, false);
  }

  return [];
}

export function filterDiaryEntriesByUser<T extends { userId?: string | null }>(
  entries: T[],
  allowedUserIds: Set<string>,
  includeAllEvents: boolean,
): T[] {
  if (includeAllEvents) return entries;
  if (allowedUserIds.size === 0) return [];
  return entries.filter((e) => {
    const uid = e.userId?.trim();
    return !!uid && allowedUserIds.has(uid);
  });
}

/** Diary event types available when creating an entry. */
export const DIARY_EVENT_TYPE_OPTIONS = [
  { value: "VIEWING", label: "Viewing" },
  { value: "GENERAL", label: "General" },
] as const;

/** Appointment status values for POST /diary-entries (`status` field). */
export const DIARY_STATUS_OPTIONS = [
  { value: "ACTIVE", label: "Active" },
  { value: "CANCELLED_BY_OFFICE", label: "CancelledByOffice" },
  { value: "CANCELLED_BY_OWNER", label: "CancelledByOwner" },
  { value: "CANCELLED_BY_APPLICANT", label: "CancelledByApplicant" },
  { value: "CANCELLED_BY_CONTACT", label: "CancelledByContact" },
] as const;

export const DIARY_CANCELLED_STATUS_VALUES = [
  "CANCELLED_BY_OFFICE",
  "CANCELLED_BY_OWNER",
  "CANCELLED_BY_APPLICANT",
  "CANCELLED_BY_CONTACT",
] as const;

export type DiaryEntryStatus = (typeof DIARY_STATUS_OPTIONS)[number]["value"];

/** `isCancelled` for POST/PATCH when status is a cancelled appointment status. */
export function diaryIsCancelledForStatus(status: string | null | undefined): boolean {
  return isDiaryCancelledDisplayStatus(status);
}

/** True when status is one of the four cancelled appointment statuses (shown struck-through on calendar/list). */
export function isDiaryCancelledDisplayStatus(status: string | null | undefined): boolean {
  if (!status?.trim()) return false;
  const normalized = status.trim().toUpperCase().replace(/-/g, "_");
  if ((DIARY_CANCELLED_STATUS_VALUES as readonly string[]).includes(normalized)) {
    return true;
  }
  const byLabel = DIARY_STATUS_OPTIONS.find(
    (o) => o.label.toLowerCase() === status.trim().toLowerCase(),
  );
  return Boolean(
    byLabel &&
      (DIARY_CANCELLED_STATUS_VALUES as readonly string[]).includes(byLabel.value),
  );
}

/** True when the entry should render struck-through: cancelled flag set, or a cancelled appointment status. */
export function isDiaryEntryCancelled(
  entry: Pick<DiaryEntrySummary, "isCancelled" | "status">,
): boolean {
  return Boolean(entry.isCancelled) || isDiaryCancelledDisplayStatus(entry.status);
}

/** Collapse duplicate rows (month-wide fetches sometimes return the same appointment more than once). */
export function dedupeDiaryEntries<
  T extends { id?: string; startDate?: string | null; externalId?: string | null },
>(entries: T[]): T[] {
  const seen = new Set<string>();
  return entries.filter((entry) => {
    const id = entry.id?.trim();
    const key =
      id ||
      `${entry.startDate ?? ""}|${(entry.externalId ?? "").trim()}`;
    if (!key || key === "|") return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** Keep only entries whose start falls on a day inside the visible calendar range (local). */
export function filterDiaryEntriesByLocalStartDateRange<
  T extends { startDate?: string | null; recurring?: boolean | null },
>(entries: T[], rangeStart: Date, rangeEnd: Date): T[] {
  return entries.filter((entry) => {
    if (!entry.startDate) return false;
    // Recurring entries are positioned by the calendar's occurrence expansion, so keep them
    // even when the original start sits outside the visible window.
    if (entry.recurring) return true;
    const start = parseApiDateTime(entry.startDate);
    if (!start) return false;
    return isLocalDateInRange(start, rangeStart, rangeEnd);
  });
}

/** Apply the Diary filters that are intentionally implemented only in the frontend. */
export function filterDiaryEntriesByDateAndCreator(
  entries: DiaryEntrySummary[],
  date: string,
  createdBy: DiaryCalendarFilterState["createdBy"],
): DiaryEntrySummary[] {
  const isDateValid = /^\d{4}-\d{2}-\d{2}$/.test(date);

  return entries.filter((entry) => {
    if (isDateValid) {
      const [year, month, day] = date.split("-").map(Number);
      const selectedDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
      const selectedDateEnd = new Date(Date.UTC(year, month - 1, day, 23, 59, 59, 999));
      const occurrenceKeys = diaryOccurrenceDateKeys(entry, selectedDate, selectedDateEnd);
      if (!occurrenceKeys.includes(date)) return false;
    }

    if (createdBy) {
      // BridgeAI-created entries carry Atlas creator audit metadata. CRM-originated
      // entries do not, so no new backend filter parameter is required.
      const origin = entry.atlasCreatedBy?.trim() ? "BRIDGE_AI" : "CRM";
      if (origin !== createdBy) return false;
    }

    return true;
  });
}

export function normalizeDiaryStatusValue(status: string | null | undefined): DiaryEntryStatus {
  const normalized = (status ?? "").trim().toUpperCase();
  const match = DIARY_STATUS_OPTIONS.find((o) => o.value === normalized);
  return match?.value ?? "ACTIVE";
}

export function diaryStatusLabel(status: string | null | undefined): string {
  const normalized = normalizeDiaryStatusValue(status);
  return DIARY_STATUS_OPTIONS.find((o) => o.value === normalized)?.label ?? normalized;
}

export function isDiaryViewingType(type: string | null | undefined): boolean {
  return (type ?? "").trim().toUpperCase() === "VIEWING";
}

/**
 * True for "meeting" category entries, mirroring the calendar's colour bucketing
 * (indigo). Viewings are explicitly excluded. Used to decide which chips get the
 * on-the-fly "Viewing: <property>" relabel — meetings only, never viewings.
 */
export function isDiaryMeetingEntry(
  entry: Pick<DiaryEntrySummary, "type" | "title">,
): boolean {
  const type = (entry.type || "").toUpperCase();
  const title = (entry.title || "").toUpperCase();
  const isViewing = type === "VIEWING" || title.includes("VIEWING");
  if (isViewing) return false;
  return type === "MEETING" || title.includes("MEETING") || title.includes("DISCUSSION");
}

function diaryEntityRef(
  entity: { id: string; sourceCrm?: string | null; externalId?: string | null },
  fallbackSourceCrm: string,
): { id: string; sourceCrm: string; externalId: string } {
  const sourceCrm = (entity.sourceCrm?.trim() || fallbackSourceCrm).trim();
  const externalId = entity.externalId?.trim();
  if (!externalId) {
    throw new Error("Selected record is missing externalId — cannot link to diary entry.");
  }
  return { id: entity.id, sourceCrm, externalId };
}

export interface BuildDiaryEntryCreateInput {
  title: string;
  type: string;
  startDate: string;
  endDate: string;
  legalEntityId: string;
  sourceCrm: string;
  /** Atlas branch UUID of the selected branch, if any. */
  branchId?: string | null;
  contact?: ContactSummary | null;
  user?: UserSummary | null;
  property?: PropertySummary | null;
  /** Sent as `viewingDetailsInternalNotes` on POST /diary-entries. */
  diaryNotes?: string | null;
}

function trimDiaryNotes(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

/** Diary notes from GET `viewingDetailsInternalNotes` (used for all appointment types). */
export function diaryInternalNotesFromEntry(entry: {
  viewingDetailsInternalNotes?: string | null;
  description?: string | null;
  viewing_details_internal_notes?: string | null;
}): string | null {
  const candidates = [
    entry.viewingDetailsInternalNotes,
    entry.viewing_details_internal_notes,
    entry.description,
  ];
  for (const value of candidates) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
}

/** Whether the entry type supports Diary Notes + Viewers Feedback tabs and internal notes on create. */
export function supportsDiaryNotesAndFeedback(type: string | null | undefined): boolean {
  return Boolean((type ?? "").trim());
}

/** @deprecated Prefer {@link supportsDiaryNotesAndFeedback}. */
export function isDiaryGeneralOrViewing(type: string | null | undefined): boolean {
  return supportsDiaryNotesAndFeedback(type);
}

/**
 * Builds POST /diary-entries body per Atlas DiaryEntry contract.
 * When a property is selected, includes properties[] (required for VIEWING, optional for GENERAL).
 */
export function buildDiaryEntryCreatePayload(input: BuildDiaryEntryCreateInput): DiaryEntryCreate {
  const type = input.type.trim().toUpperCase();
  const sourceCrm = input.sourceCrm.trim();

  const payload: DiaryEntryCreate = {
    sourceCrm,
    legalEntityId: input.legalEntityId.trim(),
    header: null,
    bridgeBranchId: null,
    instanceId: "1",
    type,
    startDate: input.startDate,
    endDate: input.endDate,
    title: input.title.trim(),
    description: null,
    status: "ACTIVE",
    isCancelled: diaryIsCancelledForStatus("ACTIVE"),
  };

  const branchId = input.branchId?.trim();
  if (branchId) {
    payload.branchId = [branchId];
  }

  if (input.user?.id) {
    payload.users = [diaryEntityRef(input.user, sourceCrm)];
  }
  if (input.contact?.id) {
    payload.contacts = [diaryEntityRef(input.contact, sourceCrm)];
  }
  if (input.property?.id) {
    payload.properties = [diaryEntityRef(input.property, sourceCrm)];
  }

  if (supportsDiaryNotesAndFeedback(type)) {
    payload.viewingDetailsInternalNotes = trimDiaryNotes(input.diaryNotes);
  }

  return payload;
}
